import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Loader2, CheckCircle2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password updated. You can sign in now.");
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <div className="relative hidden lg:block bg-[#0F172A] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?auto=format&fit=crop&q=80&w=1600"
          alt="Aviation"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-[#0F172A]/70 noise-overlay" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <img src="/paslogo.png" alt="Peregrine Companies" className="h-11 w-auto bg-white rounded-md px-3 py-2" />
            <span className="text-white font-display font-bold tracking-tight">PEREGRINE COMPANIES PORTAL</span>
          </div>
          <h1 className="text-white font-display font-extrabold text-5xl leading-[0.95] tracking-tight">
            Choose a new<br /><span className="text-[#D4AF37]">password.</span>
          </h1>
          <div className="text-xs text-zinc-400 font-mono">
            6318 Union Avenue, Shreveport, LA 71106 · 318.222.2224
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-white">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          {done ? (
            <div className="text-center" data-testid="reset-done">
              <CheckCircle2 className="h-12 w-12 text-[#10B981] mx-auto" />
              <h2 className="mt-4 font-display font-extrabold text-3xl tracking-tight text-[#0F172A]">All set</h2>
              <p className="mt-2 text-zinc-500">Your password has been updated. Redirecting to sign in…</p>
            </div>
          ) : (
            <>
              <p className="text-xs uppercase tracking-[0.25em] text-zinc-400 mb-3">New Password</p>
              <h2 className="font-display font-extrabold text-4xl tracking-tight text-[#0F172A]">Reset password</h2>
              <p className="mt-2 text-zinc-500">Enter a new password (at least 8 characters).</p>

              {!token && (
                <p className="mt-4 text-sm text-[#EF4444]" data-testid="no-token">
                  Missing reset link. Please start again from Forgot password.
                </p>
              )}

              <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="reset-form">
                <div>
                  <label className="block text-sm font-medium text-[#0F172A] mb-2">New password</label>
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="reset-password"
                    className="w-full px-4 py-3 border border-zinc-300 bg-white font-mono text-sm outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-shadow"
                    placeholder="••••••••••"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0F172A] mb-2">Confirm password</label>
                  <input
                    type="password"
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    data-testid="reset-confirm"
                    className="w-full px-4 py-3 border border-zinc-300 bg-white font-mono text-sm outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-shadow"
                    placeholder="••••••••••"
                  />
                </div>

                {error && <p className="text-sm text-[#EF4444]" data-testid="reset-error">{error}</p>}

                <button
                  type="submit"
                  disabled={submitting || !token}
                  data-testid="reset-submit"
                  className="w-full flex items-center justify-center gap-2 bg-[#0F172A] text-white py-3.5 font-medium hover:bg-[#1E293B] transition-colors disabled:opacity-60"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
                </button>
              </form>

              <Link
                to="/login"
                data-testid="back-to-login"
                className="mt-6 inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-[#0F172A] transition-colors"
              >
                <ArrowLeft className="h-4 w-4" /> Back to sign in
              </Link>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
